/* =============================================================
   ST card fit (Wrike 4557802032, card V3e round 4, Ali 11.10.2026)
   The shop images carry 6-35 % own margin around the device. This script
   measures the device box of each card image once (small canvas, pixels
   that differ from the corner colour) and passes it to CSS as custom
   properties; sportstech-custom.css section 10 then sizes and places the
   image so the device box fits the image area (never cropped).
   No CSS scale/zoom (docs/GOTCHAS.md #10): the image is only sized and
   positioned. If the image cannot be read (old browser, CORS, photo
   background), nothing changes and the standard contain fit stays.
   Results are cached in localStorage so repeat views do not jump.
   Loaded by main-collection and main-search. Rollback: remove the two
   script tags; the CSS only applies to img.st-fit.
   Round 6 (Ali 11.10.2026): the CSS applies on phones only (< 768 px);
   desktop keeps the contain fit so the hover media (desktop only, not
   measured) does not jump. The card image is still measured on desktop,
   so resizing to a phone width needs no new measurement.
   ============================================================= */
(function () {
  if (window.stCardFit) return;
  window.stCardFit = true;

  var SELECTOR = ".product-card--v3e .product-card__media > .media > img";
  var KEY = "st-card-fit-v1";
  var SIZE = 160; // canvas edge in px
  var TOLERANCE = 14; // per channel vs. corner colour (JPEG noise, #f9f9f9 backgrounds)
  var cache = {};
  try {
    cache = JSON.parse(localStorage.getItem(KEY)) || {};
  } catch (e) {
    cache = {};
  }

  function keyFor(img) {
    var src = img.currentSrc || img.getAttribute("src") || "";
    return src.replace(/^https?:/, "").replace(/[?&]width=\d+/, "");
  }

  function apply(img, box) {
    if (!box) return;
    img.style.setProperty("--st-fit-x", box[0]);
    img.style.setProperty("--st-fit-y", box[1]);
    img.style.setProperty("--st-fit-w", box[2]);
    img.style.setProperty("--st-fit-h", box[3]);
    img.style.setProperty("--st-fit-ar", box[4]);
    img.classList.add("st-fit");
  }

  function save(key, box) {
    cache[key] = box;
    try {
      if (Object.keys(cache).length > 400) cache = {};
      cache[key] = box;
      localStorage.setItem(KEY, JSON.stringify(cache));
    } catch (e) {
      /* storage full or blocked: measure again next time */
    }
  }

  function readPixels(source, w, h) {
    var canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(source, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h).data;
  }

  // Returns [x0, y1, w, h, aspect] as fractions of the image, or null.
  function measure(source) {
    var nw = source.naturalWidth;
    var nh = source.naturalHeight;
    if (!nw || !nh) return null;
    var s = SIZE / Math.max(nw, nh);
    var w = Math.max(1, Math.round(nw * s));
    var h = Math.max(1, Math.round(nh * s));
    var d = readPixels(source, w, h); // throws on a tainted canvas
    var corners = [0, (w - 1) * 4, (h - 1) * w * 4, ((h - 1) * w + w - 1) * 4];
    var bg = [d[0], d[1], d[2], d[3]];
    for (var c = 1; c < 4; c++) {
      var i = corners[c];
      // Corners disagree: photo or coloured background, keep the standard fit.
      if (Math.abs(d[i + 3] - bg[3]) > 32) return null;
      if (
        bg[3] > 16 &&
        (Math.abs(d[i] - bg[0]) > TOLERANCE ||
          Math.abs(d[i + 1] - bg[1]) > TOLERANCE ||
          Math.abs(d[i + 2] - bg[2]) > TOLERANCE)
      )
        return null;
    }
    var x0 = w;
    var y0 = h;
    var x1 = -1;
    var y1 = -1;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var p = (y * w + x) * 4;
        var isBg =
          d[p + 3] < 16 ||
          (bg[3] > 16 &&
            Math.abs(d[p] - bg[0]) <= TOLERANCE &&
            Math.abs(d[p + 1] - bg[1]) <= TOLERANCE &&
            Math.abs(d[p + 2] - bg[2]) <= TOLERANCE);
        if (isBg) continue;
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
    if (x1 < 0) return null;
    // One canvas pixel of safety on every side (anti-aliased edges, soft shadows).
    x0 = Math.max(0, x0 - 1);
    y0 = Math.max(0, y0 - 1);
    x1 = Math.min(w, x1 + 2);
    y1 = Math.min(h, y1 + 2);
    var r = function (n) {
      return Math.round(n * 1000) / 1000;
    };
    return [
      r(x0 / w),
      r(y1 / h),
      r((x1 - x0) / w),
      r((y1 - y0) / h),
      r(nw / nh),
    ];
  }

  // Same image read through CORS (other market domains load images from www.sportstech.de).
  function measureCors(img, key) {
    var probe = new Image();
    probe.crossOrigin = "anonymous";
    probe.onload = function () {
      try {
        var box = measure(probe);
        if (box) {
          save(key, box);
          apply(img, box);
        }
      } catch (e) {
        /* keep the standard fit */
      }
    };
    probe.src = img.currentSrc || img.src;
  }

  function fit(img) {
    if (img.classList.contains("st-fit") || !img.matches(SELECTOR)) return;
    var key = keyFor(img);
    if (cache[key]) {
      apply(img, cache[key]);
      return;
    }
    if (!img.complete || !img.naturalWidth) return; // the load listener comes back
    try {
      var box = measure(img);
      if (box) {
        save(key, box);
        apply(img, box);
      }
    } catch (e) {
      if (img.crossOrigin === null) measureCors(img, key);
    }
  }

  function scan() {
    document.querySelectorAll(SELECTOR).forEach(fit);
  }

  // Load does not bubble, but it can be caught on the way down: covers lazy images,
  // hover images added by hover-media.js and cards rendered after filtering.
  // scan() below applies cached boxes before the images arrive (no jump on repeat views).
  document.addEventListener(
    "load",
    function (event) {
      if (event.target.tagName === "IMG") fit(event.target);
    },
    true,
  );

  // ST chip switch (assets/st-chips-nav.js) inserts cards whose images are already loaded: no load event comes.
  document.addEventListener("collection:reloaded", scan);

  scan();
})();
